import __decorate from "../_virtual/_@oxc-project_runtime@0.147.0/helpers/esm/decorate.js";
import { LitElement, css, html, nothing } from "lit";
import { property, state } from "lit/decorators.js";
import { localized, msg } from "@lit/localize";
//#region src/pd-more-info/PdMoreInfo.ts
/**
* @license
* Copyright (c) 2021 PD Progressive Development UG. All rights reserved.
*/
var PdMoreInfo = class PdMoreInfo extends LitElement {
	constructor(..._args) {
		super(..._args);
		this.noAutoScroll = false;
		this._lessInfo = true;
		this._fullText = "";
		this._isOverflowing = false;
	}
	static {
		this.styles = [css`
      :host {
        display: block;
      }

      p {
        color: var(--pd-default-font-content-col);
        font-family: var(--pd-default-font-content-family);
        font-size: var(--pd-default-font-content-size);
        line-height: var(--pd-default-font-content-line-height, 1.4em);
        margin: 0;
      }

      a {
        cursor: pointer;
        font-family: var(--pd-default-font-link-family);
        font-size: var(--pd-default-font-link-size);
        color: var(--pd-default-font-link-col);
      }

      a:hover {
        color: var(--pd-default-font-link-col-hover);
      }

      a:focus {
        outline: none;
      }

      a:focus-visible {
        outline: 2px solid var(--pd-focus-ring-col);
        outline-offset: 2px;
        border-radius: 2px;
      }

      .content-clamped {
        display: -webkit-box;
        -webkit-box-orient: vertical;
        overflow: hidden;
      }

      .slot-hidden {
        display: none;
      }
    `];
	}
	render() {
		if (this.maxLength) return this._renderMaxLength();
		if (this.maxLines) return this._renderMaxLines();
		return this._renderLegacy();
	}
	updated() {
		if (this.maxLines && this._lessInfo) this._checkOverflow();
	}
	_renderMaxLength() {
		const needsTruncation = this._fullText.length > this.maxLength;
		const collapsed = this._lessInfo && needsTruncation;
		return html`
      <p>
        <span class=${collapsed ? "slot-hidden" : ""}
          ><slot @slotchange=${this._onSlotChange}></slot
        ></span>
        ${collapsed ? html`${this._truncateText(this._fullText, this.maxLength)}
              ${this._moreLink()}` : needsTruncation ? this._lessLink() : nothing}
      </p>
    `;
	}
	_truncateText(text, max) {
		if (text.length <= max) return text;
		const truncated = text.slice(0, max);
		const lastSpace = truncated.lastIndexOf(" ");
		return (lastSpace > 0 ? truncated.slice(0, lastSpace) : truncated) + "…";
	}
	_onSlotChange(e) {
		const text = e.target.assignedNodes({ flatten: true }).map((n) => n.textContent ?? "").join("").replace(/\s+/g, " ").trim();
		if (text !== this._fullText) this._fullText = text;
	}
	_renderMaxLines() {
		return html`
      <p>
        <span
          class=${this._lessInfo ? "content-clamped" : ""}
          style=${this._lessInfo ? `-webkit-line-clamp: ${this.maxLines}` : ""}
        >
          <slot @slotchange=${this._onMaxLinesSlotChange}></slot>
        </span>
        ${this._lessInfo && this._isOverflowing ? this._moreLink() : !this._lessInfo ? this._lessLink() : nothing}
      </p>
    `;
	}
	_onMaxLinesSlotChange() {
		this.updateComplete.then(() => this._checkOverflow());
	}
	_checkOverflow() {
		const wrapper = this.shadowRoot?.querySelector("p > span");
		if (wrapper) this._isOverflowing = wrapper.scrollHeight > wrapper.clientHeight;
	}
	_renderLegacy() {
		return html`
      <p>
        ${this._lessInfo ? html`
                <slot name="small-view"></slot>
                ${this._moreLink()}
              ` : html`
                <slot name="large-view"></slot>
                ${this._lessLink()}
              `}
      </p>
    `;
	}
	_moreLink() {
		return html`
      <a
        role="button"
        tabindex="0"
        @click=${this._showMoreInfo}
        @keydown=${this._onKeyDown}
      >
        ${msg("Mehr Informationen", { id: "pd.moreInfo.more" })}
      </a>
    `;
	}
	_lessLink() {
		return html`
      <a
        role="button"
        tabindex="0"
        @click=${this._showLessInfo}
        @keydown=${this._onKeyDown}
      >
        ${msg("Text ausblenden", { id: "pd.moreInfo.less" })}
      </a>
    `;
	}
	_onKeyDown(e) {
		if (e.key === "Enter" || e.key === " ") {
			e.preventDefault();
			e.currentTarget?.click();
		}
	}
	_showMoreInfo() {
		this._lessInfo = false;
	}
	_showLessInfo() {
		this._lessInfo = true;
		if (!this.noAutoScroll) {
			const rect = this.getBoundingClientRect();
			window.scrollBy({
				top: rect.top - 150,
				left: 0,
				behavior: "smooth"
			});
		}
	}
};
__decorate([property({
	type: Number,
	attribute: "max-length"
})], PdMoreInfo.prototype, "maxLength", void 0);
__decorate([property({
	type: Number,
	attribute: "max-lines"
})], PdMoreInfo.prototype, "maxLines", void 0);
__decorate([property({
	type: Boolean,
	reflect: true
})], PdMoreInfo.prototype, "noAutoScroll", void 0);
__decorate([state()], PdMoreInfo.prototype, "_lessInfo", void 0);
__decorate([state()], PdMoreInfo.prototype, "_fullText", void 0);
__decorate([state()], PdMoreInfo.prototype, "_isOverflowing", void 0);
PdMoreInfo = __decorate([localized()], PdMoreInfo);
//#endregion
export { PdMoreInfo };
